import wakefernLogo from "/img/integration/wakefern.png"
import { useLocation } from "wouter-preact"
import { CenterColumnContainer } from "@/components/LoginScreen.tsx"
import { LoadingSpinner } from "@/app/loading"
import {
  getIntegrations,
  Integration,
  IntegrationWithJobInfo,
  WakefernConfig,
  updateIntegration,
} from "@/api/integration"
import { ConnectLogo } from "@/setup/connect/connect-logo"
import { Form } from "@/components/form/Form.tsx"
import { useFormState } from "@/components/form/form_state.tsx"
import { Input } from "@/components/form/Input.tsx"
import { Button } from "@/components/ui/button.tsx"
import { toast } from "@/components/ui/use-toast"
import { useState } from "preact/compat"
import { useAsyncEffect } from "@/utils"
import { EmailSelector } from "@/setup/config/email-config-page"

interface WakefernConfigFormProps {
  integration: Integration
  emailAccounts: IntegrationWithJobInfo[]
  onSuccess: (int: Integration) => void
}

export function WakefernConfigForm({ integration, emailAccounts, onSuccess }: WakefernConfigFormProps) {
  const [loading, setLoading] = useState(false)
  const data = integration.data as unknown as WakefernConfig
  const [selectedEmail, setSelectedEmail] = useState(data.dispute_from_email ?? "")

  const form = useFormState({
    dispute_from_name: data.dispute_from_name ?? "",
    discount_term: data.discount_term?.toString() ?? "",
  })

  async function onSubmit() {
    setLoading(true)
    form.set_form_errors([])

    const discountTerm = parseInt(form.discount_term.value, 10)
    const config: WakefernConfig = {
      dispute_from_email: selectedEmail || null,
      dispute_from_name: form.dispute_from_name.value || null,
      discount_term: isNaN(discountTerm) ? null : discountTerm,
    }

    const res = await updateIntegration(integration.id, { data: config })
    setLoading(false)
    if (!res.ok) {
      form.set_form_errors(["Failed to save configuration. Please try again."])
      return
    }
    toast({
      title: "Settings saved",
      description: "Your Wakefern configuration has been updated.",
    })
    onSuccess(res.value.data)
  }

  return (
    <Form class="space-y-6" onSubmit={onSubmit} alerts={form.form_alerts}>
      <EmailSelector
        emailAccounts={emailAccounts}
        selectedEmail={selectedEmail}
        onSelect={setSelectedEmail}
        label="Email Account for Disputes"
        description="Choose which email account to use when sending disputes to Wakefern."
      />

      <Input
        label="Sender Name"
        type="text"
        name="dispute_from_name"
        placeholder="Your Company Name"
        bind={form.dispute_from_name}
      />
      <p class="text-gray-500 text-sm -mt-4">This name will appear in dispute emails sent to Wakefern.</p>

      <Input
        label="Discount Term (days)"
        type="number"
        name="discount_term"
        placeholder="10"
        bind={form.discount_term}
      />
      <p class="text-gray-500 text-sm -mt-4">
        Number of days within which an invoice payment qualifies as early-pay. Used to color-code the "payment days"
        column on the Early Pay view. Defaults to 10 when left blank.
      </p>

      <Button type="submit" disabled={loading || !selectedEmail}>
        {loading ? "Saving..." : "Save Configuration"}
      </Button>
    </Form>
  )
}

export function ConfigWakefernPage() {
  const [, setLocation] = useLocation()
  const [integration, setIntegration] = useState<Integration | null>(null)
  const [emailAccounts, setEmailAccounts] = useState<IntegrationWithJobInfo[]>([])
  const [loading, setLoading] = useState(true)

  useAsyncEffect(async () => {
    const res = await getIntegrations()
    if (res.ok) {
      const allIntegrations = res.value.data
      const emails = allIntegrations.filter(i => ["gmail", "microsoft"].includes(i.name))
      setEmailAccounts(emails)

      const wakefernIntegration = allIntegrations.find(i => i.name === "wakefern")
      if (wakefernIntegration) {
        setIntegration(wakefernIntegration)
      }
    }
    setLoading(false)
  }, [])

  if (loading) {
    return (
      <CenterColumnContainer>
        <ConnectLogo src={wakefernLogo} alt="Wakefern" />
        <div class="flex justify-center py-8">
          <LoadingSpinner />
        </div>
      </CenterColumnContainer>
    )
  }

  if (!integration) {
    return (
      <CenterColumnContainer>
        <ConnectLogo src={wakefernLogo} alt="Wakefern" />
        <div class="text-center py-8">
          <p class="text-gray-500 mb-4">No Wakefern integration found. Please connect Wakefern first.</p>
          <Button
            class="bg-gray-900 text-white rounded-lg hover:bg-gray-800"
            onClick={() => setLocation("/setup/connect/wakefern")}>
            Connect Wakefern
          </Button>
        </div>
      </CenterColumnContainer>
    )
  }

  return (
    <CenterColumnContainer>
      <ConnectLogo src={wakefernLogo} alt="Wakefern" />
      <WakefernConfigForm
        integration={integration}
        emailAccounts={emailAccounts}
        onSuccess={() => setLocation("/setup/connect")}
      />
    </CenterColumnContainer>
  )
}
